import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowPathIcon,
  ChevronDownIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import Button from '../shared/Button';
import { cn } from '../../utils/cn';
import { useAuth } from '../../context/AuthContext';
import { useStation } from '../../context/StationContext';
import {
  addModelCatalog,
  DEFAULT_ORG_MODEL,
  DEFAULT_ORG_PROVIDER,
  defaultOrgAssignment,
  deleteModelCatalog,
  deleteOrgSettings,
  fetchModelCatalog,
  fetchOrgSettings,
  fetchProviders,
  getSelectedOrg,
  isDatabaseUnavailableError,
  modelsForProvider,
  reloadLlmGateway,
  saveOrgSettings,
  setSelectedOrg,
  type ModelCatalogEntry,
  type ProviderInfo,
} from '../../utils/llmSettingsApi';
import type { Station } from '../../types';

const PROVIDER_IDS = ['openrouter', 'cerebras', 'ollama'] as const;

const selectCls =
  'w-full bg-app-bg border border-app-border rounded-lg px-3 py-2 text-sm text-app-text focus:ring-2 focus:ring-app-accent/30 outline-none';

const inputCls =
  'w-full bg-app-bg border border-app-border rounded-lg px-3 py-2 text-sm text-app-text placeholder:text-app-text-dim focus:ring-2 focus:ring-app-accent/30 outline-none';

function stationOrgName(station: Station | undefined): string {
  if (!station) return '';
  return String((station as Station & { org_name?: string }).org_name ?? '').trim();
}

export default function LLMSettingsForm() {
  const { t } = useTranslation();
  const { userOrgName } = useAuth();
  const { stations, activeStationId } = useStation();

  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [catalog, setCatalog] = useState<ModelCatalogEntry[]>([]);

  const [selectedOrg, setSelectedOrgState] = useState(() => getSelectedOrg());
  const [editProvider, setEditProvider] = useState(DEFAULT_ORG_PROVIDER);
  const [editModel, setEditModel] = useState(DEFAULT_ORG_MODEL);

  const [showModelList, setShowModelList] = useState(false);
  const [addProvider, setAddProvider] = useState(DEFAULT_ORG_PROVIDER);
  const [addModel, setAddModel] = useState('');
  const [delProvider, setDelProvider] = useState(DEFAULT_ORG_PROVIDER);
  const [delModel, setDelModel] = useState('');

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<'idle' | 'ok' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [dbUnavailable, setDbUnavailable] = useState(false);

  const activeStation = useMemo(
    () => stations.find((s) => s.id === activeStationId),
    [stations, activeStationId],
  );

  const orgChoices = useMemo(() => {
    const names = new Set<string>();
    const login = (userOrgName || '').trim();
    if (login) names.add(login);
    for (const s of stations) {
      const o = stationOrgName(s);
      if (o) names.add(o);
    }
    const saved = getSelectedOrg();
    if (saved) names.add(saved);
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [userOrgName, stations]);

  const editModelOptions = modelsForProvider(catalog, editProvider);
  const delModelOptions = modelsForProvider(catalog, delProvider);
  const showOrgPicker = orgChoices.length > 1;

  const flash = useCallback((ok: boolean, msg: string) => {
    setStatus(ok ? 'ok' : 'error');
    setMessage(msg);
    setTimeout(() => {
      setStatus('idle');
      setMessage('');
    }, 3500);
  }, []);

  const resolveDefaultOrg = useCallback((): string => {
    const login = (userOrgName || '').trim();
    if (login) return login;
    const fromStation = stationOrgName(activeStation);
    if (fromStation) return fromStation;
    return orgChoices[0] ?? '';
  }, [userOrgName, activeStation, orgChoices]);

  const loadOrgSettings = useCallback(async (org: string) => {
    if (!org) {
      const d = defaultOrgAssignment();
      setEditProvider(d.provider);
      setEditModel(d.model);
      return;
    }
    try {
      const row = await fetchOrgSettings(org);
      if (row) {
        setEditProvider(row.provider);
        setEditModel(row.model);
      } else {
        const d = defaultOrgAssignment();
        setEditProvider(d.provider);
        setEditModel(d.model);
      }
    } catch {
      const d = defaultOrgAssignment();
      setEditProvider(d.provider);
      setEditModel(d.model);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    setDbUnavailable(false);
    try {
      const [prov, models] = await Promise.all([fetchProviders(), fetchModelCatalog()]);
      setProviders(prov);
      setCatalog(models);

      const saved = getSelectedOrg();
      const pick = resolveDefaultOrg();
      const org = saved && orgChoices.includes(saved) ? saved : pick;
      if (org) {
        setSelectedOrgState(org);
        setSelectedOrg(org);
        await loadOrgSettings(org);
      }
    } catch (e) {
      const msg = String(e);
      if (isDatabaseUnavailableError(msg)) setDbUnavailable(true);
      flash(false, msg);
    } finally {
      setLoading(false);
    }
  }, [flash, loadOrgSettings, orgChoices, resolveDefaultOrg]);

  useEffect(() => {
    void refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    const opts = modelsForProvider(catalog, delProvider);
    if (opts.length > 0 && !opts.some((o) => o.model === delModel)) {
      setDelModel(opts[0].model);
    }
  }, [catalog, delProvider, delModel]);

  async function onOrgChange(org: string) {
    setSelectedOrgState(org);
    setSelectedOrg(org);
    setLoading(true);
    await loadOrgSettings(org);
    setLoading(false);
  }

  async function handleSaveOrg() {
    if (!selectedOrg) {
      flash(false, t('settings.llm.noOrgWarning'));
      return;
    }
    const model = editModel.trim() || DEFAULT_ORG_MODEL;
    setBusy(true);
    try {
      await saveOrgSettings(selectedOrg, { provider: editProvider, model });
      await loadOrgSettings(selectedOrg);
      flash(true, t('settings.llm.saved'));
    } catch (e) {
      const msg = String(e);
      if (isDatabaseUnavailableError(msg)) setDbUnavailable(true);
      flash(false, msg);
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteOrg() {
    if (!selectedOrg) return;
    if (!window.confirm(t('settings.llm.confirmDelete', { org: selectedOrg }))) return;
    setBusy(true);
    try {
      await deleteOrgSettings(selectedOrg);
      const d = defaultOrgAssignment();
      setEditProvider(d.provider);
      setEditModel(d.model);
      flash(true, t('settings.llm.deleted'));
    } catch (e) {
      flash(false, String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleApply() {
    setBusy(true);
    try {
      await reloadLlmGateway();
      if (selectedOrg) await loadOrgSettings(selectedOrg);
      flash(true, t('settings.llm.applied'));
    } catch (e) {
      flash(false, String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleAddModel() {
    const model = addModel.trim();
    if (!model) {
      flash(false, t('settings.llm.modelRequired'));
      return;
    }
    setBusy(true);
    try {
      await addModelCatalog(addProvider, model);
      setAddModel('');
      await refreshAll();
      flash(true, t('settings.llm.modelAdded'));
    } catch (e) {
      flash(false, String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteModel() {
    if (!delModel) return;
    if (!window.confirm(t('settings.llm.confirmDeleteModel', { provider: delProvider, model: delModel }))) {
      return;
    }
    setBusy(true);
    try {
      await deleteModelCatalog(delProvider, delModel);
      await refreshAll();
      flash(true, t('settings.llm.modelDeleted'));
    } catch (e) {
      flash(false, String(e));
    } finally {
      setBusy(false);
    }
  }

  const providerEnvHint = (id: string) => {
    const p = providers.find((x) => x.id === id);
    if (!p?.base_url) return t('settings.llm.envNotConfigured');
    return p.configured ? t('settings.llm.envOk') : t('settings.llm.envMissingKey');
  };

  const envStatusClass = (id: string) => {
    const p = providers.find((x) => x.id === id);
    if (!p?.base_url) return 'bg-app-bg text-app-text-dim border-app-border';
    return p.configured
      ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/25'
      : 'bg-amber-500/10 text-amber-800 dark:text-amber-200 border-amber-500/25';
  };

  return (
    <div className="px-5 py-5 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3 pb-1 border-b border-app-border/60">
        <p className="text-xs text-app-text-dim">{t('settings.llm.envBarHint')}</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void refreshAll()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-app-border text-xs text-app-text-sec hover:bg-app-bg"
            disabled={loading}
          >
            <ArrowPathIcon className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
            {t('settings.llm.refreshList')}
          </button>
          <Button onClick={() => void handleApply()} variant="secondary" size="sm" disabled={dbUnavailable || busy}>
            {t('settings.llm.apply')}
          </Button>
        </div>
      </div>

      {dbUnavailable && (
        <div
          role="alert"
          className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-200"
        >
          <p className="font-medium">{t('settings.llm.dbRequiredTitle')}</p>
          <p className="mt-1 text-xs">{t('settings.llm.dbRequiredHint')}</p>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-app-text-dim py-8 text-center">{t('common.loading')}</p>
      ) : !selectedOrg && orgChoices.length === 0 ? (
        <p className="text-sm text-app-text-dim py-8 text-center">{t('settings.llm.noOrgForUser')}</p>
      ) : (
        <div className="space-y-5">
          <div className="rounded-xl border border-app-border bg-app-bg/50 p-4 sm:p-5">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-5">
              <div className="min-w-0 flex-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-app-text-dim">
                  {t('settings.llm.orgLabel')}
                </span>
                {showOrgPicker ? (
                  <select
                    value={selectedOrg}
                    onChange={(e) => void onOrgChange(e.target.value)}
                    className={cn(selectCls, 'mt-1.5 font-medium')}
                    disabled={busy}
                  >
                    {orgChoices.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="mt-1 text-xl font-semibold text-app-text truncate">{selectedOrg}</p>
                )}
              </div>
              {selectedOrg && (
                <div className="shrink-0 rounded-lg bg-app-surface border border-app-border px-3 py-2 text-xs">
                  <span className="text-app-text-dim">{t('settings.llm.inUse')}</span>
                  <p className="font-mono text-app-text mt-0.5 truncate max-w-[14rem] sm:max-w-xs">
                    {editProvider} / {editModel}
                  </p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-app-text mb-1.5">
                  {t('settings.llm.providerSelect')}
                </label>
                <select
                  value={editProvider}
                  onChange={(e) => {
                    setEditProvider(e.target.value);
                    const opts = modelsForProvider(catalog, e.target.value);
                    setEditModel(opts[0]?.model ?? DEFAULT_ORG_MODEL);
                  }}
                  className={selectCls}
                  disabled={dbUnavailable || busy}
                >
                  {PROVIDER_IDS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-app-text mb-1.5">
                  {t('settings.llm.modelSelect')}
                </label>
                {editModelOptions.length > 0 ? (
                  <select
                    value={editModel}
                    onChange={(e) => setEditModel(e.target.value)}
                    className={selectCls}
                    disabled={dbUnavailable || busy}
                  >
                    {editModelOptions.map((e) => (
                      <option key={e.model} value={e.model}>
                        {e.model}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={editModel}
                    onChange={(e) => setEditModel(e.target.value)}
                    placeholder={t('settings.llm.modelPlaceholder')}
                    className={inputCls}
                    disabled={dbUnavailable || busy}
                  />
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-app-border">
              <Button
                onClick={() => void handleSaveOrg()}
                variant="primary"
                size="sm"
                disabled={dbUnavailable || busy || !selectedOrg}
              >
                {t('settings.llm.assignOrg')}
              </Button>
              <Button
                onClick={() => void handleDeleteOrg()}
                variant="ghost"
                size="sm"
                disabled={dbUnavailable || busy || !selectedOrg}
              >
                {t('settings.llm.deleteOrg')}
              </Button>
            </div>
          </div>

          <details
            className="rounded-xl border border-app-border bg-app-bg/30 group"
            open={showModelList}
            onToggle={(e) => setShowModelList(e.currentTarget.open)}
          >
            <summary className="flex items-center gap-2 px-4 py-3 cursor-pointer text-sm font-medium text-app-text-sec hover:text-app-text list-none [&::-webkit-details-marker]:hidden">
              <ChevronDownIcon className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180" />
              {t('settings.llm.modelListSection')}
            </summary>
            <div className="px-4 pb-4 space-y-4 border-t border-app-border pt-4">
              <p className="text-xs text-app-text-dim">{t('settings.llm.modelListHint')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-[8rem_1fr_auto] gap-2 sm:items-end">
                <select
                  value={addProvider}
                  onChange={(e) => setAddProvider(e.target.value)}
                  className={selectCls}
                  disabled={dbUnavailable || busy}
                >
                  {PROVIDER_IDS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={addModel}
                  onChange={(e) => setAddModel(e.target.value)}
                  placeholder={t('settings.llm.modelPlaceholder')}
                  className={inputCls}
                  disabled={dbUnavailable || busy}
                />
                <Button
                  onClick={() => void handleAddModel()}
                  variant="primary"
                  size="sm"
                  disabled={dbUnavailable || busy}
                  icon={<PlusIcon className="h-4 w-4" />}
                  className="sm:justify-self-start"
                >
                  {t('settings.llm.addOrgConfirm')}
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[8rem_1fr_auto] gap-2 sm:items-end">
                <select
                  value={delProvider}
                  onChange={(e) => {
                    setDelProvider(e.target.value);
                    setDelModel(modelsForProvider(catalog, e.target.value)[0]?.model ?? '');
                  }}
                  className={selectCls}
                  disabled={dbUnavailable || busy}
                >
                  {PROVIDER_IDS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
                <select
                  value={delModel}
                  onChange={(e) => setDelModel(e.target.value)}
                  className={selectCls}
                  disabled={dbUnavailable || busy || delModelOptions.length === 0}
                >
                  {delModelOptions.map((e) => (
                    <option key={e.model} value={e.model}>
                      {e.model}
                    </option>
                  ))}
                </select>
                <Button
                  onClick={() => void handleDeleteModel()}
                  variant="danger"
                  size="sm"
                  disabled={dbUnavailable || busy || !delModel}
                  icon={<TrashIcon className="h-4 w-4" />}
                >
                  {t('settings.llm.deleteModel')}
                </Button>
              </div>
            </div>
          </details>
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        <span className="text-[10px] font-medium text-app-text-dim w-full sm:w-auto sm:mr-1">
          {t('settings.llm.envFootnote')}
        </span>
        {PROVIDER_IDS.map((id) => (
          <span
            key={id}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium',
              envStatusClass(id),
            )}
          >
            <span className="font-mono">{id}</span>
            <span className="opacity-80">·</span>
            <span>{providerEnvHint(id)}</span>
          </span>
        ))}
      </div>

      {status !== 'idle' && message && (
        <div
          role="status"
          className={cn(
            'rounded-lg border px-4 py-2.5 text-sm',
            status === 'error'
              ? 'border-red-500/30 bg-red-500/5 text-red-600'
              : 'border-emerald-500/30 bg-emerald-500/5 text-emerald-700',
          )}
        >
          {message}
        </div>
      )}
    </div>
  );
}
